import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, Filter, X, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ProfessionalCard } from '../components/professionals/ProfessionalCard';
import { SearchFilters } from '../components/professionals/SearchFilters';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [professionals, setProfessionals] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    estado: searchParams.get('estado') || '',
    rango: searchParams.get('rango') || '',
    especialidad: searchParams.get('especialidad') || '',
    estatus: searchParams.get('estatus') || ''
  });

  // Fetch filter options
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await fetch(`${API}/filter-options`);
        if (response.ok) {
          setFilterOptions(await response.json());
        }
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };
    fetchFilterOptions();
  }, []);

  // Fetch professionals
  const fetchProfessionals = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.estado) params.append('estado', filters.estado);
      if (filters.rango) params.append('rango', filters.rango);
      if (filters.especialidad) params.append('especialidad', filters.especialidad);
      if (filters.estatus) params.append('estatus', filters.estatus);

      const response = await fetch(`${API}/professionals?${params.toString()}`);
      if (response.ok) {
        setProfessionals(await response.json());
      }
    } catch (error) {
      console.error('Error fetching professionals:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchProfessionals();
  }, [fetchProfessionals]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      estado: '',
      rango: '',
      especialidad: '',
      estatus: ''
    });
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="page-transition bg-slate-50 min-h-screen" data-testid="search-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="container-cni py-8">
          <h1 className="font-manrope font-bold text-2xl md:text-3xl text-slate-900 mb-2">
            Buscar Profesional
          </h1>
          <p className="text-slate-600">
            Encuentra profesionales inmobiliarios certificados y verificados
          </p>
        </div>
      </div>

      <div className="container-cni py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Desktop Filters Sidebar */}
          <aside className="hidden lg:block lg:col-span-3">
            <div className="filter-sidebar">
              <SearchFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                filterOptions={filterOptions}
                onClearFilters={handleClearFilters}
              />
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-9">
            {/* Mobile Filter Button */}
            <div className="lg:hidden mb-4">
              <Sheet open={isMobileFilterOpen} onOpenChange={setIsMobileFilterOpen}>
                <SheetTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full border-slate-200 text-slate-700"
                    data-testid="mobile-filter-btn"
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    Filtros
                    {activeFilterCount > 0 && (
                      <span className="ml-2 bg-slate-900 text-white text-xs px-2 py-0.5 rounded-full">
                        {activeFilterCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-full sm:max-w-md p-0">
                  <SheetHeader className="p-6 border-b border-slate-200">
                    <SheetTitle className="font-manrope">Filtros de búsqueda</SheetTitle>
                  </SheetHeader>
                  <div className="p-6 overflow-y-auto max-h-[calc(100vh-100px)]">
                    <SearchFilters
                      filters={filters}
                      onFilterChange={(key, value) => {
                        handleFilterChange(key, value);
                      }}
                      filterOptions={filterOptions}
                      onClearFilters={() => {
                        handleClearFilters();
                        setIsMobileFilterOpen(false);
                      }}
                    />
                    <Button 
                      className="w-full mt-4 bg-amber-500 hover:bg-amber-400 text-black font-bold"
                      onClick={() => setIsMobileFilterOpen(false)}
                    >
                      Ver resultados
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Results Header */}
            <div className="flex justify-between items-center mb-6">
              <p className="text-sm text-slate-600">
                {isLoading ? (
                  'Buscando...'
                ) : (
                  <>
                    <span className="font-semibold text-slate-900">{professionals.length}</span>
                    {' '}profesionales encontrados
                  </>
                )}
              </p>
              
              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilters}
                  className="text-slate-600 hover:text-slate-900"
                  data-testid="clear-all-filters-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Limpiar filtros
                </Button>
              )}
            </div>

            {/* Results Grid */}
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : professionals.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="no-results">
                <SearchIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-manrope font-semibold text-lg text-slate-900 mb-2">
                  No se encontraron resultados
                </h3>
                <p className="text-slate-600 mb-6">
                  Intenta ajustar los filtros de búsqueda
                </p>
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                  className="border-slate-200"
                >
                  Limpiar filtros
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="professionals-grid">
                {professionals.map((professional, index) => (
                  <div 
                    key={professional.id}
                    className="animate-fade-in"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <ProfessionalCard professional={professional} />
                  </div>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};
